import { isAppDebugEnabled, logExpandedDebug } from '@/utils/debug/expandedConsoleLog';
import { moduleDefectApi } from '@/apis/module.defect.api';
import { appPerfMark, appPerfMeasure } from '@/utils/appBootstrapPerfLog.js';

export const DEFECT_MODULE_VERIFY_PREFIX = '[DEFECT-MODULE-VERIFY]';

/**
 * 자동 전체스캔(결함×모듈 폴리곤)은 전용 플래그를 켠 로컬 serve에서만 실행한다.
 * `build:aws` / staging / solarwiz 는 NODE_ENV=production.
 * production에서는 window 브리지도 설치하지 않는다.
 */
export function isDefectModuleGeoVerifyAutoScanEnabled() {
  return (
    isAppDebugEnabled() &&
    process.env.VUE_APP_ENABLE_DEFECT_MODULE_GEO_VERIFY === 'true'
  );
}

let instructionsPrinted = false;

/**
 * @param {string} event
 * @param {Record<string, unknown>} [detail]
 */
export function logDefectModuleVerify(event, detail = {}) {
  logExpandedDebug(DEFECT_MODULE_VERIFY_PREFIX, event, detail);
}

/**
 * DevTools 필터: DEFECT-MODULE-VERIFY
 */
export function printDefectModuleVerifyInstructions() {
  if (!isAppDebugEnabled()) {
    return;
  }
  if (instructionsPrinted) {
    return;
  }
  instructionsPrinted = true;

  logDefectModuleVerify('USER-STEPS', {
    filter: 'DEFECT-MODULE-VERIFY',
    step1: '대시보드 → 리포트 선택 → 우측 「모듈리스트」 ON (siteDesign 폴리곤 540개 로드)',
    step2: '결함 마커 ON — 필요하면 콘솔에서 verifyAll(20)으로 수동 batch-report 확인',
    step3: '줌을 최대로 올린 뒤, 핀 끝이 모듈 채우기 안에 들어가 보이는 결함을 클릭 → click-report',
    step4: '콘솔에서 __solvitDefectModuleVerify.verifyOne(결함seq) — 예: verifyOne(49020)',
    step5: '콘솔에서 __solvitDefectModuleVerify.verifyAll(20) — 샘플 20건 일괄',
    howToRead: {
      mismatchInsideButOrphan:
        '클라이언트 폴리곤 포함인데 DB solarModuleSeq null → 지도는 맞아 보이나 findContainingPoint 실패(서버/PostGIS 의심)',
      outsideAllModules:
        'WFS 핀 좌표가 어떤 모듈 폴리곤에도 안 들어감 → IR 박스만 맞거나 좌표 계산 어긋남',
      linkedAndInside: '정상',
      linkedButOutside: 'DB FK는 있는데 WFS 점이 폴리곤 밖(geometry_name 등 WFS 이슈 의심)'
    },
    manualApi: '__solvitDefectModuleVerify.{ printInstructions, verifyOne, verifyAll, relinkOrphans, lastBatchReport }'
  });
}

const EARTH_RADIUS_M = 6371000;

/**
 * @param {number} lon1
 * @param {number} lat1
 * @param {number} lon2
 * @param {number} lat2
 */
export function haversineDistanceMeters(lon1, lat1, lon2, lat2) {
  const toRad = deg => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

/**
 * @param {number} lon
 * @param {number} lat
 * @param {number[][]} ring [[lng,lat],...]
 */
export function isPointInPolygonRing(lon, lat, ring) {
  if (!Array.isArray(ring) || ring.length < 3) {
    return false;
  }
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = Number(ring[i][0]);
    const yi = Number(ring[i][1]);
    const xj = Number(ring[j][0]);
    const yj = Number(ring[j][1]);
    if (!Number.isFinite(xi) || !Number.isFinite(yi) || !Number.isFinite(xj) || !Number.isFinite(yj)) {
      continue;
    }
    const intersect =
      yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi + 0.0) + xi;
    if (intersect) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * @param {number} lon
 * @param {number} lat
 * @param {number[][]} ring
 */
export function minDistancePointToPolygonRingMeters(lon, lat, ring) {
  if (!Array.isArray(ring) || ring.length < 2) {
    return Infinity;
  }

  if (isPointInPolygonRing(lon, lat, ring)) {
    return 0;
  }

  let minDist = Infinity;
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const ax = Number(a[0]);
    const ay = Number(a[1]);
    const bx = Number(b[0]);
    const by = Number(b[1]);
    if (![ax, ay, bx, by].every(Number.isFinite)) {
      continue;
    }

    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = 0;
    if (lenSq > 0) {
      t = Math.max(0, Math.min(1, ((lon - ax) * dx + (lat - ay) * dy) / lenSq));
    }
    const px = ax + t * dx;
    const py = ay + t * dy;
    const dist = haversineDistanceMeters(lon, lat, px, py);
    if (dist < minDist) {
      minDist = dist;
    }
  }

  for (const pt of ring) {
    const dist = haversineDistanceMeters(lon, lat, Number(pt[0]), Number(pt[1]));
    if (dist < minDist) {
      minDist = dist;
    }
  }

  return minDist;
}

/**
 * OL Feature[] → 모듈 카탈로그
 * @param {import('ol/Feature').default[]} features
 */
export function buildModuleCatalogFromOlFeatures(features) {
  const list = Array.isArray(features) ? features : [];
  const catalog = [];

  for (const feature of list) {
    const props = feature?.getProperties?.() ?? {};
    const seq = props.solar_module_seq ?? props.solarModuleSeq ?? null;
    if (seq == null || seq === '') {
      continue;
    }
    const geometry = feature?.getGeometry?.();
    const coords = geometry?.getCoordinates?.();
    const ring = Array.isArray(coords?.[0]) ? coords[0] : null;
    if (!ring || ring.length < 3) {
      continue;
    }
    catalog.push({
      solarModuleSeq: seq,
      moduleId: props.module_id ?? props.moduleId ?? null,
      stringId: props.string_id ?? props.stringId ?? null,
      arrayId: props.array_id ?? props.arrayId ?? null,
      ring
    });
  }

  return catalog;
}

/**
 * @param {{ seq?: number, lng?: number, lat?: number, solarModuleSeq?: number|string|null }} defect
 * @param {ReturnType<typeof buildModuleCatalogFromOlFeatures>} moduleCatalog
 */
export function analyzeDefectGeoMatch(defect, moduleCatalog) {
  const lon = Number(defect?.lng);
  const lat = Number(defect?.lat);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
    return {
      ok: false,
      reason: 'missing_lng_lat',
      defectSeq: defect?.seq ?? null
    };
  }

  const dbSolarModuleSeq =
    defect?.solarModuleSeq != null && defect?.solarModuleSeq !== ''
      ? String(defect.solarModuleSeq)
      : null;

  const containing = [];
  let nearest = null;

  for (const mod of moduleCatalog || []) {
    const inside = isPointInPolygonRing(lon, lat, mod.ring);
    if (inside) {
      containing.push({
        solarModuleSeq: mod.solarModuleSeq,
        moduleId: mod.moduleId
      });
    }
    const distM = minDistancePointToPolygonRingMeters(lon, lat, mod.ring);
    if (!nearest || distM < nearest.distanceMeters) {
      nearest = {
        solarModuleSeq: mod.solarModuleSeq,
        moduleId: mod.moduleId,
        distanceMeters: Math.round(distM * 100) / 100
      };
    }
  }

  const clientInside = containing.length > 0;
  let verdict = 'outside_all_modules';
  if (dbSolarModuleSeq && clientInside) {
    const dbInside = containing.some(c => String(c.solarModuleSeq) === dbSolarModuleSeq);
    verdict = dbInside ? 'linked_and_inside' : 'linked_but_outside_client_polygon';
  } else if (dbSolarModuleSeq && !clientInside) {
    verdict = 'linked_but_outside_client_polygon';
  } else if (!dbSolarModuleSeq && clientInside) {
    verdict = 'mismatch_inside_but_orphan';
  }

  return {
    ok: true,
    defectSeq: defect?.seq ?? null,
    wgs84: { lon, lat },
    dbSolarModuleSeq,
    clientInsideModule: clientInside,
    containingModules: containing.slice(0, 5),
    containingModuleCount: containing.length,
    nearestModule: nearest,
    within2m: nearest != null && nearest.distanceMeters <= 2,
    verdict,
    verdictHint:
      verdict === 'mismatch_inside_but_orphan'
        ? '지도(siteDesign 폴리곤) 기준 포함인데 DB FK 없음 → findContainingPoint/ST_Within 쪽 의심'
        : verdict === 'outside_all_modules'
          ? `WFS 핀은 모든 모듈 밖(가장 가까운 모듈 ${nearest?.distanceMeters ?? '?'}m) → IR 착시 또는 defectLocation 계산 의심`
          : null
  };
}

/**
 * @param {object[]} defects
 * @param {ReturnType<typeof buildModuleCatalogFromOlFeatures>} moduleCatalog
 * @param {{ sampleLimit?: number, trigger?: string }} [options]
 */
export function runDefectModuleGeoVerificationBatch(defects, moduleCatalog, options = {}) {
  const sampleLimit = options.sampleLimit ?? 15;
  const list = Array.isArray(defects) ? defects : [];
  const modules = Array.isArray(moduleCatalog) ? moduleCatalog : [];
  appPerfMark('defectGeoVerify:batch:start', {
    trigger: options.trigger ?? 'batch',
    defectCount: list.length,
    moduleCount: modules.length
  });

  if (!modules.length) {
    const report = {
      trigger: options.trigger ?? 'batch',
      skipped: true,
      reason: 'no_module_polygons — 모듈리스트 ON 후 siteDesign 폴리곤 로드 필요'
    };
    logDefectModuleVerify('batch-report-skipped', report);
    return report;
  }

  const analyzed = [];
  let withLngLat = 0;
  let clientInside = 0;
  let dbLinked = 0;
  let mismatchInsideOrphan = 0;
  let outsideAll = 0;

  for (const defect of list) {
    const result = analyzeDefectGeoMatch(defect, modules);
    if (!result.ok) {
      continue;
    }
    withLngLat += 1;
    if (result.clientInsideModule) {
      clientInside += 1;
    }
    if (result.dbSolarModuleSeq) {
      dbLinked += 1;
    }
    if (result.verdict === 'mismatch_inside_but_orphan') {
      mismatchInsideOrphan += 1;
    }
    if (result.verdict === 'outside_all_modules') {
      outsideAll += 1;
    }
    analyzed.push(result);
  }

  const mismatchSamples = analyzed
    .filter(r => r.verdict === 'mismatch_inside_but_orphan')
    .slice(0, sampleLimit);
  const outsideSamples = analyzed
    .filter(r => r.verdict === 'outside_all_modules')
    .sort((a, b) => (b.nearestModule?.distanceMeters ?? 0) - (a.nearestModule?.distanceMeters ?? 0))
    .slice(0, Math.min(5, sampleLimit));
  const insideSamples = analyzed
    .filter(r => r.clientInsideModule)
    .slice(0, 5)
    .map(r => ({ ...r, containingModules: [...(r.containingModules || [])] }));

  const report = {
    trigger: options.trigger ?? 'batch',
    modulePolygonCount: modules.length,
    defectRowCount: list.length,
    analyzedWithLngLat: withLngLat,
    summary: {
      clientInsideModuleCount: clientInside,
      dbLinkedCount: dbLinked,
      mismatchInsideButOrphan: mismatchInsideOrphan,
      outsideAllModules: outsideAll,
      insideButOrphanRate:
        withLngLat > 0 ? Math.round((mismatchInsideOrphan / withLngLat) * 1000) / 10 : 0
    },
    interpretation:
      mismatchInsideOrphan > 0
        ? '지도 폴리곤 기준으로는 모듈 안인데 DB orphan → 서버 findContainingPoint 문제 가능성 큼'
        : outsideAll === withLngLat && withLngLat > 0
          ? 'WFS 핀 좌표가 모듈 폴리곤 밖 → IR/열화상에서 맞아 보여도 지도 점은 따로 놀 수 있음'
          : '샘플 확인 필요',
    samples: {
      mismatchInsideButOrphan: mismatchSamples,
      outsideFarthest: outsideSamples,
      anyInside: insideSamples
    }
  };

  appPerfMeasure('defectGeoVerify:batch:done', 'defectGeoVerify:batch:start', {
    trigger: options.trigger ?? 'batch',
    defectCount: list.length,
    moduleCount: modules.length,
    analyzedWithLngLat: withLngLat
  });

  logDefectModuleVerify('batch-report', report);

  const s = report.summary;
  console.warn(
    `${DEFECT_MODULE_VERIFY_PREFIX} batch-summary`,
    `defects=${report.analyzedWithLngLat}`,
    `modules=${report.modulePolygonCount}`,
    `inside=${s.clientInsideModuleCount}`,
    `dbLinked=${s.dbLinkedCount}`,
    `MISMATCH_insideButOrphan=${s.mismatchInsideButOrphan}`,
    `outside=${s.outsideAllModules}`,
    `→ ${report.interpretation}`
  );

  if (mismatchSamples.length > 0) {
    console.table(
      mismatchSamples.slice(0, 5).map(r => ({
        defectSeq: r.defectSeq,
        lng: r.wgs84?.lon,
        lat: r.wgs84?.lat,
        moduleSeq: r.containingModules?.[0]?.solarModuleSeq,
        moduleId: r.containingModules?.[0]?.moduleId
      }))
    );
  }

  return report;
}

/**
 * @param {number|string} defectSeq
 * @param {object[]} defects
 * @param {ReturnType<typeof buildModuleCatalogFromOlFeatures>} moduleCatalog
 * @param {{ trigger?: string }} [options]
 */
export function verifySingleDefectGeoMatch(defectSeq, defects, moduleCatalog, options = {}) {
  printDefectModuleVerifyInstructions();

  const list = Array.isArray(defects) ? defects : [];
  const row =
    list.find(d => Number(d?.seq) === Number(defectSeq)) ||
    list.find(d => String(d?.seq) === String(defectSeq));

  if (!row) {
    const report = {
      trigger: options.trigger ?? 'verifyOne',
      defectSeq,
      ok: false,
      reason: 'defect_not_in_wfsDefectRows'
    };
    logDefectModuleVerify('one-report-not-found', report);
    return report;
  }

  if (!moduleCatalog?.length) {
    const report = {
      trigger: options.trigger ?? 'verifyOne',
      defectSeq,
      ok: false,
      reason: 'no_module_polygons'
    };
    logDefectModuleVerify('one-report-skipped', report);
    return report;
  }

  const analysis = analyzeDefectGeoMatch(row, moduleCatalog);
  const report = {
    trigger: options.trigger ?? 'verifyOne',
    ...analysis,
    storeRow: {
      seq: row.seq,
      solarModuleSeq: row.solarModuleSeq ?? null,
      moduleId: row.moduleId ?? null,
      lng: row.lng,
      lat: row.lat,
      thermalImgSeq: row.thermalImgSeq ?? null
    },
    nextAction:
      analysis.verdict === 'mismatch_inside_but_orphan'
        ? '서버 ST_Within / findContainingPoint + shape_geog SRID 확인 (지도=클라 포함, DB=null)'
        : analysis.verdict === 'outside_all_modules'
          ? '줌 최대에서 WFS 핀 끝 위치 확인 — IR 박스와 핀 좌표 불일치 가능'
          : '정상 또는 DB FK만 확인'
  };

  logDefectModuleVerify('one-report', report);

  console.warn(
    `${DEFECT_MODULE_VERIFY_PREFIX} one-summary`,
    `seq=${report.defectSeq}`,
    `verdict=${report.verdict}`,
    report.wgs84 ? `wgs84=${report.wgs84.lon},${report.wgs84.lat}` : '',
    report.containingModules?.[0]
      ? `clientModule=${report.containingModules[0].moduleId}(seq=${report.containingModules[0].solarModuleSeq})`
      : report.nearestModule
        ? `nearest=${report.nearestModule.moduleId}@${report.nearestModule.distanceMeters}m`
        : '',
    `dbSolarModuleSeq=${report.dbSolarModuleSeq ?? 'null'}`,
    `→ ${report.nextAction ?? report.verdictHint ?? ''}`
  );

  return report;
}

let lastBatchReport = null;
let getVerifyContext = null;
let lastBatchRunKey = '';
let lastBatchRunAt = 0;
const BATCH_DEBOUNCE_MS = 4000;

/**
 * @param {() => { defects?: object[], moduleFeatures?: import('ol/Feature').default[] }} contextFn
 */
export function installDefectModuleGeoVerifyBridge(contextFn) {
  if (!isAppDebugEnabled()) {
    return;
  }
  getVerifyContext = contextFn;

  if (typeof window === 'undefined') {
    return;
  }

  window.__solvitDefectModuleVerify = {
    printInstructions: printDefectModuleVerifyInstructions,
    verifyAll: (limit = 20) => {
      printDefectModuleVerifyInstructions();
      const ctx = getVerifyContext?.() ?? {};
      const catalog = buildModuleCatalogFromOlFeatures(ctx.moduleFeatures || []);
      lastBatchRunKey = '';
      lastBatchReport = runDefectModuleGeoVerificationBatch(ctx.defects || [], catalog, {
        sampleLimit: limit,
        trigger: 'manual-verifyAll'
      });
      return lastBatchReport;
    },
    verifyOne: defectSeq => {
      const ctx = getVerifyContext?.() ?? {};
      const catalog = buildModuleCatalogFromOlFeatures(ctx.moduleFeatures || []);
      return verifySingleDefectGeoMatch(defectSeq, ctx.defects || [], catalog, {
        trigger: 'manual-verifyOne'
      });
    },
    relinkOrphans: async reportId => {
      if (reportId == null || reportId === '') {
        console.warn(`${DEFECT_MODULE_VERIFY_PREFIX} relinkOrphans — reportId 필요`);
        return null;
      }
      const result = await moduleDefectApi.relinkOrphanModules(reportId);
      console.warn(`${DEFECT_MODULE_VERIFY_PREFIX} relink-result`, result);
      return result;
    },
    lastBatchReport: () => lastBatchReport
  };

  printDefectModuleVerifyInstructions();
}

/**
 * @param {object[]} defects
 * @param {import('ol/Feature').default[]} moduleFeatures
 * @param {string} trigger
 */
export function maybeRunDefectModuleGeoVerification(defects, moduleFeatures, trigger = 'auto') {
  if (!isDefectModuleGeoVerifyAutoScanEnabled()) {
    return null;
  }

  const catalog = buildModuleCatalogFromOlFeatures(moduleFeatures);
  if (!catalog.length || !defects?.length) {
    return null;
  }

  const runKey = `${defects.length}:${catalog.length}:${trigger}`;
  const now = Date.now();
  if (
    trigger !== 'manual-verifyAll' &&
    runKey === lastBatchRunKey &&
    now - lastBatchRunAt < BATCH_DEBOUNCE_MS
  ) {
    return lastBatchReport;
  }
  lastBatchRunKey = runKey;
  lastBatchRunAt = now;

  lastBatchReport = runDefectModuleGeoVerificationBatch(defects, catalog, {
    trigger,
    sampleLimit: 15
  });
  return lastBatchReport;
}
